"use client";

import { User } from "@/gql/graphql";
import { CRAFTMAN } from "@/graphql/craftman";
import useLang from "@/hooks/useLang";
import { parseProfileUrl } from "@/utils/getProfileUrl";
import { decodeId } from "@/utils/url-encoder";
import { useLazyQuery } from "@apollo/client";
import { usePathname, useSearchParams } from "next/navigation";
import {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type ProfileContextType = {
  loading: boolean;
  craftman: User | undefined;
  totalFavoris: number | undefined;
  totalOrders: number | undefined;
  totalNotices: number | undefined;
  materials: string[] | undefined;
  items: string[] | undefined;
  category: string;
  profilePhotoUrl: string | undefined;
  bannerPhotoUrl: string | undefined;
  showPhotos: boolean;
  setShowPhotos: Dispatch<SetStateAction<boolean>>;
  showItems: boolean;
  setShowItems: Dispatch<SetStateAction<boolean>>;
  showMaterials: boolean;
  setShowMaterials: Dispatch<SetStateAction<boolean>>;
  isFavoris: boolean | undefined;
  id?: number;
  setId: Dispatch<SetStateAction<number | undefined>>;
  nameToProfile?: string;
  setNameToProfile: Dispatch<SetStateAction<string | undefined>>;
  categoryToProfile?: string;
  setCategoryToProfile: Dispatch<SetStateAction<string | undefined>>;
};

const ProfileContext = createContext<ProfileContextType | undefined>(undefined);

export const ProfileProvider = ({ children }: { children: ReactNode }) => {
  const { lang } = useLang();

  // get & decode ref(id encoded) in the url
  const searchParams = useSearchParams();
  const encodedRef = searchParams.get("ref");
  const ref = encodedRef ? decodeId(encodedRef) : undefined;

  const [nameToProfile, setNameToProfile] = useState<string | undefined>(
    undefined
  );
  const [categoryToProfile, setCategoryToProfile] = useState<
    string | undefined
  >(undefined);

  const [id, setId] = useState<number | undefined>(undefined);
  const [showPhotos, setShowPhotos] = useState(true);
  const [showItems, setShowItems] = useState(false);
  const [showMaterials, setShowMaterials] = useState(false);

  const pathname = usePathname();
  const { category: slugCategoryFromUrl, name: slugNameFromUrl } =
    parseProfileUrl(pathname);

  useEffect(() => {
    if (slugCategoryFromUrl && slugNameFromUrl) {
      console.log(
        `Parsed category: ${slugCategoryFromUrl}, name: ${slugNameFromUrl}`
      );
    } else {
      console.warn("Failed to parse category or name from URL");
    }
  }, [slugCategoryFromUrl, slugNameFromUrl]);

  //const categoryFromUrl = guessOriginalFromSlug(slugCategoryFromUrl);
  //const nameFromUrl = guessOriginalFromSlug(slugNameFromUrl);

  // const variablesIfId = {
  //   where: {
  //     id: id,
  //   },
  // };

  const variablesIfRef = {
    where: {
      id: Number(ref)
    },
  };

  const [getCraftman, { data, loading }] = useLazyQuery<{ user: User }>(CRAFTMAN, {
    variables: variablesIfRef,
    fetchPolicy: "cache-and-network",
  });

  useEffect(() => {
    if (ref) {
      getCraftman()
    }
  }, [getCraftman, ref])

  const craftman = useMemo(() => data?.user, [data?.user]);

  const totalFavoris = craftman?._count?.CraftmanFavorites;
  const totalOrders = craftman?._count?.CraftmanOrders;
  const totalNotices = craftman?._count?.NoticeOrders;

  const customLang = useMemo(() => (lang === "fr" ? "fr" : "en"), [lang]);

  const materials = useMemo(() => {
    return craftman?.MaterialTypes.map((el) => el[`name_${customLang}`]);
  }, [craftman?.MaterialTypes, customLang]);

  const items = useMemo(() => {
    return craftman?.ItemTypes.map((el) => el[`name_${customLang}`]);
  }, [craftman?.ItemTypes, customLang]);

  const category = useMemo(() => {
    return craftman?.Categories.length
      ? `${craftman?.Categories[0][`name_${customLang}`]} / `
      : "";
  }, [craftman?.Categories, customLang]);

  const profilePhotoUrl = useMemo(() => {
    const profilePhoto = craftman?.ProfilePhoto?.url;
    return profilePhoto || "";
  }, [craftman?.ProfilePhoto?.url]);

  const bannerPhotoUrl = useMemo(() => {
    const bannerPhoto = craftman?.BannerPhoto?.url;
    return bannerPhoto;
  }, [craftman?.BannerPhoto?.url]);

  const isFavoris = craftman?.isFavoris;

  useEffect(() => {
    const storedId = sessionStorage.getItem("profileId");
    if (storedId) {
      setId(Number(storedId));
    }
  }, []);

  return (
    <ProfileContext.Provider
      value={{
        loading,
        craftman,
        totalFavoris,
        totalOrders,
        totalNotices,
        materials,
        items,
        category,
        profilePhotoUrl,
        bannerPhotoUrl,
        showPhotos,
        setShowPhotos,
        showItems,
        setShowItems,
        showMaterials,
        setShowMaterials,
        isFavoris,
        id,
        setId,
        nameToProfile,
        setNameToProfile,
        categoryToProfile,
        setCategoryToProfile,
      }}
    >
      {children}
    </ProfileContext.Provider>
  );
};

export const useProfileContext = () => {
  const context = useContext(ProfileContext);
  if (!context) {
    throw new Error("useProfileContext must be used within a ProfileProvider");
  }
  return context;
};
